import { BaseService } from "./BaseService";
import { ErrorResponse, BuildxConfig } from "../types/index";

/**
 * Flows service for Buildx
 * Handles flow types and running flows
 * 
 * @example
 * ```typescript
 * const flows = buildx.flows();
 * 
 * // Get flow types
 * const types = await flows.getTypes();
 * 
 * // Run a flow
 * const result = await flows.run('flow-id', 'session-id', 'root', { arg1: 'value' });
 * ```
 */
export class Flows {
	private baseService: BaseService;

	constructor(config: BuildxConfig) {
		this.baseService = new BaseService(config);
	}

	updateConfig(config: BuildxConfig): void {
		this.baseService.updateConfig(config);
	}

	/**
	 * Get all flow types
	 */
	async getTypes(): Promise<any | ErrorResponse> {
		// Optionally use cache in the future
		return await this.baseService.get("/flows/types");
	}

	/**
	 * Run a flow
	 */
	async run(flowId: string, sessionId: string | null = null, state: any = "root", args: any = {}, projectId?: string): Promise<any | ErrorResponse> {
		const url = this.baseService.buildProjectUrl(projectId, `/flows/${flowId}/run`);
		return await this.baseService.post(url, { session_id: sessionId, state, args });
	}

	/**
	 * Get GPT flow code suggestions
	 */
	async getGptFlowSuggestions(instruction: string, projectId?: string): Promise<any | ErrorResponse> {
		const url = this.baseService.buildProjectUrl(projectId, "/ai/gpt/flow/node");
		return await this.baseService.post<any>(url, { instruction });
	}

	/**
	 * Get GPT collection suggestions
	 */
	async getGptCollectionSuggestions(instruction: string, projectId?: string): Promise<any | ErrorResponse> {
		const url = this.baseService.buildProjectUrl(projectId, "/ai/gpt/collections");
		return await this.baseService.post<any>(url, { instruction });
	}

	/**
	 * Get GPT lifecycle suggestions
	 */
	async getGptLifecycleSuggestions(instruction: string, projectId?: string): Promise<any | ErrorResponse> {
		const url = this.baseService.buildProjectUrl(projectId, "/ai/gpt/lifecycle");
		return await this.baseService.post<any>(url, { instruction });
	}
} 